import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BlobField from '../components/BlobField';
import api, { formatApiErrorDetail } from '../api';

/**
 * Sandbox checkout page — stub-mode only.
 * Mimics NowPayments' hosted checkout: shows the deposit summary,
 * a fake pay-to address + amount, and a single button that
 * "Simulate payment received" → credits Points → sends the user
 * back to /wallet?deposit={id} which shows the confirmation toast.
 */

// Deterministic-ish fake address per currency
const FAKE_ADDR = {
  btc: 'bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh',
  eth: '0xd8dA6BF26964aF9D7eEd9e03E53415D37aA96045',
  usdttrc20: 'TG4b3jV8xJ8kQAwGkbCTBpZbAhWzP2jYyR',
  usdcerc20: '0x28C6c06298d514Db089934071355E5743bf21d60',
};

export default function StubCheckout() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deposit, setDeposit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/wallet/deposit/${id}`)
      .then(({ data }) => setDeposit(data.deposit))
      .catch((ex) => setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Deposit not found.'));
  }, [id]);

  const simulate = async () => {
    if (busy) return;
    setBusy(true);
    setErr('');
    try {
      await api.post(`/wallet/deposit/${id}/simulate`);
      navigate(`/wallet?deposit=${id}`, { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || 'Simulation failed.');
      setBusy(false);
    }
  };

  const cancel = () => navigate('/wallet');

  if (err && !deposit) {
    return (
      <>
        <BlobField />
        <div className="sc-wrap">
          <div className="sc-card" data-testid="stub-checkout-err">
            <h2 className="sc-title">Something went wrong</h2>
            <p style={{ color: 'var(--text-secondary)' }}>{err}</p>
            <button className="sc-btn secondary" onClick={cancel}>Back to Wallet</button>
          </div>
        </div>
      </>
    );
  }

  if (!deposit) {
    return (
      <>
        <BlobField />
        <div className="loading-shell" data-testid="stub-checkout-loading">loading checkout…</div>
      </>
    );
  }

  const alreadyDone = deposit.status === 'finished';
  const cur = deposit.pay_currency.toUpperCase().replace('TRC20', ' TRC').replace('ERC20', ' ERC');
  const addr = FAKE_ADDR[deposit.pay_currency] || FAKE_ADDR.btc;

  return (
    <>
      <BlobField />
      <div className="sc-wrap" data-testid="stub-checkout">
        <div className="sc-badge">SANDBOX MODE · No real crypto moves</div>

        <div className="sc-card">
          <div className="sc-brand">
            <div className="sc-logo">◎</div>
            <div>
              <div className="sc-brand-name">Clanster · Test Payment Portal</div>
              <div className="sc-brand-sub">Mock of a NowPayments hosted checkout</div>
            </div>
          </div>

          <h2 className="sc-title">Pay <span>{deposit.estimated_amount} {cur}</span></h2>
          <p className="sc-sub">≈ ${deposit.usd_amount.toFixed(2)} USD · You will receive <b>{deposit.usd_amount.toFixed(2)} pts</b></p>

          <div className="sc-addr-block" data-testid="stub-checkout-address">
            <div className="sc-addr-label">Send exactly {deposit.estimated_amount} {cur} to</div>
            <div className="sc-addr-value">{addr}</div>
            <div className="sc-addr-hint">Copy-paste this into your wallet, then hit the button below.</div>
          </div>

          <div className="sc-timer" data-testid="stub-checkout-timer">
            🕒 Real invoices expire in 30 minutes. Sandbox invoices never expire.
          </div>

          {err && <div className="sc-err">{err}</div>}

          {alreadyDone ? (
            <button className="sc-btn primary" onClick={() => navigate(`/wallet?deposit=${id}`)} data-testid="stub-checkout-done">
              ✓ Payment received — back to Wallet
            </button>
          ) : (
            <>
              <button
                className="sc-btn primary"
                onClick={simulate}
                disabled={busy}
                data-testid="stub-checkout-simulate-btn"
              >
                {busy ? 'Confirming…' : '✅ Simulate payment received'}
              </button>
              <button className="sc-btn secondary" onClick={cancel} data-testid="stub-checkout-cancel-btn">
                Cancel & back to Wallet
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
